import "./history.css";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pagination, PaginationItem } from "@mui/material";
import Heading from "../fluxo-engenheiro/heading/heading";
import { AppDispatch, RootState } from "../../redux/store";
import AccordionList from "../accordionList/AccordionList";
import AlertError from "../../assets/svgs/alert-error.svg";
import AlertSuccess from "../../assets/svgs/alert-success.svg";
import AlertInfo from "../../assets/svgs/alert-info.svg";
import { formatDate } from "../../utils/DateFunctions.js";
import { getClassificationHistory } from "../../redux/actions/fluxoNSimilar";
import ArrowNext from "../../assets/svgs/arrow-next.svg";
import { useRoles } from "../../hooks/roles";
import { countBusinessDaysBetween } from "../../utils/businessDays";

interface HistoryMessage {
  status: string;
  date?: string | null;
  createdAt?: string | null;
  changedBy?: {
    userId: string | null;
    email: string | null;
    group: string | null;
  } | null;
  responsible?: {
    userId: string | null;
    email: string | null;
    group: string | null;
    status?: string | null;
  } | null;
  responsibleEmail?: string | null;
  division?: string | null;
  group?: string | null;
}

interface HistoryItem {
  message: HistoryMessage;
}

const PAGE_SIZE = 10;
const FINAL_DISPLAY_STATUSES = new Set(["FINALIZADO", "RECLASSIFICADO"]);

const getPersonValue = (
  person?: { email: string | null; userId: string | null } | null,
  fallback?: string | null,
) => person?.email ?? person?.userId ?? fallback ?? "N/A";

const getGroupValue = (group?: string | null) => group ?? "N/A";

const ArrowBackIcon = () => (
  <img
    src={ArrowNext}
    alt="Página anterior"
    style={{ transform: "rotate(180deg)" }}
  />
);

const ArrowNextIcon = () => <img src={ArrowNext} alt="Próxima página" />;

const History = () => {
  const dispatch = useDispatch<AppDispatch>();
  const sessionMaterial = useSelector(
    (state: RootState) => state.sessionClassifications?.sessionMaterial,
  );
  const sessionClassificationHistory = useSelector(
    (state: RootState) =>
      state.sessionClassifications?.sessionClassificationHistory,
  );
  const { isTax, isEngineer } = useRoles();

  const [currentPage, setCurrentPage] = useState(1);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [previousPageLastCreatedAtByPage, setPreviousPageLastCreatedAtByPage] =
    useState<Record<number, string | null>>({});

  const classificationId = sessionMaterial?.classificationId;

  const statusMap: Record<
    string,
    {
      label: string;
      color: string;
      icon: string;
    }
  > = {
    NOVO: {
      label: "Novo",
      color: "#979EA4",
      icon: AlertError,
    },
    RECLASSIFICAR: {
      label: "Reclassificar",
      color: "#9e2896",
      icon: AlertError,
    },
    EM_CLASSIFICACAO: {
      label: "Em Classificação",
      color: "#9DC9FF",
      icon: AlertInfo,
    },
    FINALIZADO: {
      label: "Processo Finalizado",
      color: "#00884A",
      icon: AlertSuccess,
    },
    ENTREGUE_SAP: {
      label: "Entregue SAP",
      color: "#003E64",
      icon: AlertSuccess,
    },
    PENDENTE_VALIDACAO_FISCAL: {
      label: "Pendente de Validação Fiscal",
      color: "#A1DFDB",
      icon: AlertInfo,
    },
    PENDENTE_GRUPO: {
      label: "Pendente de Grupo",
      color: "#F97316",
      icon: AlertInfo,
    },
    EM_ANALISE: {
      label: "Em Análise",
      color: "#EEC100",
      icon: AlertInfo,
    },
    RECLASSIFICADO: {
      label: "Reclassificado",
      color: "#FF9D9D",
      icon: AlertSuccess,
    },
    SUSPENSO: {
      label: "Suspenso",
      color: "#E8B6E3",
      icon: AlertInfo,
    },
  };

  useEffect(() => {
    if (!classificationId) return;

    setLoadingHistory(true);
    dispatch(
      getClassificationHistory({
        classificationId,
        page: currentPage - 1,
        size: PAGE_SIZE,
      }),
    ).finally(() => setLoadingHistory(false));
  }, [classificationId, currentPage, dispatch]);

  const historyContent: HistoryItem[] =
    sessionClassificationHistory?.content ?? [];

  const totalPages = useMemo(() => {
    const pages = sessionClassificationHistory?.page?.totalPages;
    return pages && pages > 0 ? pages : 1;
  }, [sessionClassificationHistory?.page?.totalPages]);

  useEffect(() => {
    if (!classificationId || currentPage <= 1 || historyContent.length === 0) {
      return;
    }

    if (Object.hasOwn(previousPageLastCreatedAtByPage, currentPage)) {
      return;
    }

    dispatch(
      getClassificationHistory({
        classificationId,
        page: currentPage - 2,
        size: PAGE_SIZE,
      }),
    ).then((action: any) => {
      const previousPageContent = action?.payload?.content ?? [];
      const lastItemCreatedAt =
        previousPageContent[previousPageContent.length - 1]?.message
          ?.date ??
        previousPageContent[previousPageContent.length - 1]?.message
          ?.createdAt ??
        null;

      setPreviousPageLastCreatedAtByPage((prev) => ({
        ...prev,
        [currentPage]: lastItemCreatedAt,
      }));
    });
  }, [
    classificationId,
    currentPage,
    dispatch,
    historyContent.length,
    previousPageLastCreatedAtByPage,
  ]);

  const getReferenceCreatedAt = (index: number): string | undefined => {
    const isMostRecentItem = index === 0;

    if (isMostRecentItem && currentPage === 1) {
      return new Date().toISOString();
    }

    if (isMostRecentItem) {
      return previousPageLastCreatedAtByPage[currentPage] ?? undefined;
    }

    return (
      historyContent[index - 1]?.message?.date ??
      historyContent[index - 1]?.message?.createdAt ??
      undefined
    );
  };

  const isDuplicatedFinalForEngineer = (index: number) => {
    if (!isEngineer || isTax) return false;
    if (historyContent[index]?.message?.status !== "ENTREGUE_SAP") return false;
    return (
      historyContent[index - 1]?.message?.status === "FINALIZADO" ||
      historyContent[index + 1]?.message?.status === "FINALIZADO"
    );
  };

  const accordionItems = historyContent.flatMap((item, index) => {
    if (isDuplicatedFinalForEngineer(index)) return [];

    const statusCode = item?.message?.status;
    const displayStatusCode =
      statusCode === "ENTREGUE_SAP" && isEngineer && !isTax
        ? "FINALIZADO"
        : statusCode;
    const status = statusMap[displayStatusCode];
    const isFinalDisplayStatus = FINAL_DISPLAY_STATUSES.has(displayStatusCode);
    const createdAt = item?.message?.date ?? item?.message?.createdAt ?? undefined;
    const referenceCreatedAt = getReferenceCreatedAt(index);
    const businessDays = countBusinessDaysBetween(
      createdAt,
      referenceCreatedAt,
    );
    const actor = getPersonValue(item?.message?.changedBy);
    const actorGroup = getGroupValue(item?.message?.changedBy?.group);
    const expectedResponsible = getPersonValue(
      item?.message?.responsible,
      item?.message?.responsibleEmail,
    );
    const expectedResponsibleGroup = getGroupValue(
      item?.message?.responsible?.group ?? item?.message?.division,
    );

    return [{
      id: `${statusCode}-${createdAt}-${index}`,
      title: (
        <div
          className={`accordionTitle${isFinalDisplayStatus ? " accordionTitleWithoutDays" : ""}`}
        >
          <span>{createdAt ? formatDate(createdAt, true) : "-"}</span>
          <span>{status?.label || displayStatusCode}</span>
          {!isFinalDisplayStatus && (
            <span className="utilsDays">{businessDays} dia(s)</span>
          )}
        </div>
      ),
      content: (
        <div className="accordionContent">
          <div className="accordionContentRow">
            <p>Ator: {actor}</p>
            <span className="historyGroupChip">Perfil: {actorGroup}</span>
          </div>
          <div className="accordionContentRow">
            <p>Responsável esperado: {expectedResponsible}</p>
            <span className="historyGroupChip">
              Perfil: {expectedResponsibleGroup}
            </span>
          </div>
        </div>
      ),
      indicatorColor: status?.color || "",
      icon: status?.icon || AlertInfo,
    }];
  });

  return (
    <>
      <div className="containerHomePage">
        <Heading
          titlePage="Histórico da Classificação"
          simpleHeading={true}
          materialData={{
            sessionId: sessionMaterial?.sessionId,
            productCode: sessionMaterial?.productCode,
            productSimilarCode: sessionMaterial?.productSimilarCode,
            center: sessionMaterial?.center,
            classificationStatus: sessionMaterial?.classificationStatus?.name,
            classificationID: sessionMaterial?.classificationId,
            ncmCode: sessionMaterial?.sessionMetadata?.outputFile?.ncmCode,
          }}
          isTax={isTax}
        />
      </div>

      <div className="calcContainerHistory">
        <div className="mainContentHistory">
          <div className="titleHistory">
            <h4>Histórico</h4>
          </div>

          <div className="contentHistory">
            {loadingHistory ? (
              <p>Carregando histórico...</p>
            ) : (
              <>
                <AccordionList items={accordionItems} />

                {accordionItems.length === 0 && (
                  <p>Nenhum histórico encontrado para esta classificação.</p>
                )}

                <div
                  style={{
                    marginTop: "16px",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Pagination
                    count={totalPages}
                    siblingCount={2}
                    onChange={(_event, newPage) => setCurrentPage(newPage)}
                    color="primary"
                    page={currentPage}
                    renderItem={(item) => (
                      <PaginationItem
                        slots={{ previous: ArrowBackIcon, next: ArrowNextIcon }}
                        {...item}
                      />
                    )}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default History;
